import { useState, useEffect } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Form from '@/form';
import TextField from '@/form/TextField';
import OptionField from '@/form/OptionField';
import TextSelectField from '@/form/TextSelectField';
import {
  CertificateEntry,
  Skill,
  SkillsAPI,
  SkillUpdateData,
  Uniform,
} from '@/services/skillsAPI';

export interface Props {
  skill: Skill;
  hideModal: () => void;
  onSuccess: (updatedSkill: Skill) => void;
}

interface FormValues {
  display_name_en: string;
  display_name_es: string;
  ai_enabled: boolean;
  english_only: boolean;
  gravy_trained: boolean;
  requestable: boolean;
  popular_pick: boolean;
  default_uniform_id: string;
}

const SkillEditModal = ({ skill, hideModal, onSuccess }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uniforms, setUniforms] = useState<Uniform[]>([]);

  // Requirements state (managed outside form since it's a dynamic list)
  const [requirements, setRequirements] = useState<string[]>(
    skill.requirements || [],
  );
  const [newRequirement, setNewRequirement] = useState('');

  // Certificate state (managed outside form since it's a dynamic list)
  const [certificates, setCertificates] = useState<CertificateEntry[]>(
    skill.certificate || [],
  );
  const [newCertType, setNewCertType] = useState('');
  const [newCertLabel, setNewCertLabel] = useState('');

  const CERTIFICATE_TYPES = [
    { value: 'food_handling', label: 'Food Handling Certificate' },
    { value: 'alcohol_handling', label: 'Alcohol Handling Certificate' },
    { value: 'driver_license', label: "Driver's License" },
  ];

  useEffect(() => {
    SkillsAPI.getUniforms().then((res) => {
      if (res.success) setUniforms(res.data);
    });
  }, []);

  const initialValues: FormValues = {
    display_name_en: skill.display_name_en || '',
    display_name_es: skill.display_name_es || '',
    ai_enabled: skill.ai_enabled,
    english_only: skill.english_only,
    gravy_trained: skill.gravy_trained,
    requestable: skill.requestable,
    popular_pick: skill.popular_pick,
    default_uniform_id: skill.default_uniform_id
      ? String(skill.default_uniform_id)
      : '',
  };

  const handleAddRequirement = () => {
    const trimmed = newRequirement.trim();
    if (trimmed && !requirements.includes(trimmed)) {
      setRequirements([...requirements, trimmed]);
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleCertTypeChange = (value: string) => {
    setNewCertType(value);
    const found = CERTIFICATE_TYPES.find((ct) => ct.value === value);
    if (found) setNewCertLabel(found.label);
  };

  const handleAddCertificate = () => {
    if (!newCertType) return;
    const alreadyAdded = certificates.some((c) => c.type === newCertType);
    if (alreadyAdded) return;
    const label =
      newCertLabel.trim() ||
      CERTIFICATE_TYPES.find((ct) => ct.value === newCertType)?.label ||
      newCertType;
    setCertificates([...certificates, { type: newCertType, label }]);
    setNewCertType('');
    setNewCertLabel('');
  };

  const handleRemoveCertificate = (index: number) => {
    setCertificates(certificates.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const updateData: SkillUpdateData = {
        display_name_en: values.display_name_en,
        display_name_es: values.display_name_es,
        ai_enabled: values.ai_enabled,
        english_only: values.english_only,
        gravy_trained: values.gravy_trained,
        requestable: values.requestable,
        popular_pick: values.popular_pick,
        default_uniform_id: values.default_uniform_id
          ? Number(values.default_uniform_id)
          : null,
        requirements,
        certificate: certificates.length > 0 ? certificates : null,
      };

      const response = await SkillsAPI.updateSkill(skill.id, updateData);

      if (response.success) {
        onSuccess(response.data);
        hideModal();
      } else {
        setError('Failed to update skill');
      }
    } catch (err: any) {
      console.error('Error updating skill:', err);
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.response?.data?.error ||
          'Failed to update skill',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const uniformOptions = [
    { label: 'None', value: '' },
    ...uniforms.map((u) => ({ label: u.name, value: String(u.id) })),
  ];

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
  };

  const tagStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: '#f0f0f0',
    borderRadius: '4px',
    fontSize: '13px',
  };

  const removeBtn: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#999',
    fontSize: '16px',
    lineHeight: 1,
    padding: 0,
  };

  return (
    <Modal
      disableClickout
      size="md"
      title={`Edit Skill: ${skill.name}`}
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            status="danger"
            title="Error"
            onDismiss={() => setError(null)}
          />
        )}
        <Form initialValues={initialValues} onSubmit={handleSubmit}>
          <Stack gap={16} vertical>
            <TextField
              required
              fieldId="display_name_en"
              label="Display Name (English)"
              placeholder="Enter display name in English"
            />
            <TextField
              fieldId="display_name_es"
              label="Display Name (Spanish)"
              placeholder="Enter display name in Spanish"
            />
            <Stack gap={24}>
              <OptionField
                appearance="switch"
                fieldId="ai_enabled"
                label="AI Enabled"
              />
              <OptionField
                appearance="switch"
                fieldId="english_only"
                label="English Only"
              />
            </Stack>
            <Stack gap={24}>
              <OptionField
                appearance="switch"
                fieldId="gravy_trained"
                label="Gravy Trained"
              />
              <OptionField
                appearance="switch"
                fieldId="requestable"
                label="Requestable"
              />
            </Stack>
            <Stack gap={24}>
              <OptionField
                appearance="switch"
                fieldId="popular_pick"
                label="Popular Pick"
              />
            </Stack>

            {/* Default Uniform */}
            <TextSelectField
              fieldId="default_uniform_id"
              label="Default Uniform"
              options={uniformOptions}
            />

            {/* Requirements */}
            <div>
              <Small weight="medium">Requirements</Small>
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                {requirements.map((req, i) => (
                  <span key={i} style={tagStyle}>
                    {req}
                    <button
                      type="button"
                      style={removeBtn}
                      onClick={() => handleRemoveRequirement(i)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <Stack gap={8} style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  placeholder="Add a requirement..."
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddRequirement();
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <Button
                  a11yLabel="Add requirement"
                  appearance="outline"
                  label="Add"
                  size="sm"
                  type="button"
                  onClick={handleAddRequirement}
                />
              </Stack>
            </div>

            {/* Certificates */}
            <div>
              <Small weight="medium">Certificates</Small>
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {certificates.map((cert, i) => (
                  <span key={i} style={tagStyle}>
                    <strong>{cert.type}</strong>
                    {cert.label !== cert.type && ` — ${cert.label}`}
                    <button
                      type="button"
                      style={removeBtn}
                      onClick={() => handleRemoveCertificate(i)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <Stack gap={8} style={{ marginTop: '8px' }}>
                <select
                  value={newCertType}
                  onChange={(e) => handleCertTypeChange(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Select certificate type...</option>
                  {CERTIFICATE_TYPES.filter(
                    (ct) => !certificates.some((c) => c.type === ct.value),
                  ).map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Custom label (optional)"
                  value={newCertLabel}
                  onChange={(e) => setNewCertLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCertificate();
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <Button
                  a11yLabel="Add certificate"
                  appearance="outline"
                  label="Add"
                  size="sm"
                  type="button"
                  onClick={handleAddCertificate}
                />
              </Stack>
            </div>

            <Stack justify="end">
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                type="button"
                onClick={hideModal}
              />
              <Button
                a11yLabel="Save skill"
                id="btn-submit"
                isLoading={isLoading}
                label="Save"
                type="submit"
              />
            </Stack>
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default SkillEditModal;
